import React, { useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { APPS } from '../constants/apps.js';
import Papa from 'papaparse';
import { importLeadsCsv, previewImport } from '../api/leads.js';
import { queryKeys } from '../lib/queryKeys';
import { TagPicker } from './TagPicker.jsx';


function parseInstagramUrl(url) {
    if (!url) return null;
    const trimmed = url.trim();
    if (trimmed.startsWith('@')) return trimmed.slice(1);
    const match = trimmed.match(/instagram\.com\/([^/?#]+)/);
    if (match) return match[1].replace(/\/$/, '');
    return trimmed;
}

const CRM_FIELDS = [
    { value: 'email', label: 'Email' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'name', label: 'Имя' },
    { value: 'geo', label: 'Гео' },
    { value: 'tiktok', label: 'TikTok' },
    { value: 'phone', label: 'Телефон' },
];

function autoDetectMappings(headers) {
    const mappings = {};
    for (const header of headers) {
        const h = header.toLowerCase();
        let detected = '';
        if (h.includes('email') || h.includes('e-mail') || h.includes('почта') || h.includes('mail')) {
            detected = 'email';
        } else if (h.includes('instagram') || h.includes('insta') || h === 'ig') {
            detected = 'instagram';
        } else if (h.includes('geo') || h.includes('гео') || h.includes('country') || h.includes('страна') || h.includes('город') || h.includes('city') || h.includes('region')) {
            detected = 'geo';
        } else if (h.includes('name') || h.includes('имя') || h.includes('first_name') || h.includes('firstname')) {
            detected = 'name';
        } else if (h.includes('tiktok') || h.includes('tt')) {
            detected = 'tiktok';
        } else if (h.includes('phone') || h.includes('телефон') || h.includes('тел') || h.includes('mobile') || h.includes('whatsapp')) {
            detected = 'phone';
        }
        if (detected && !Object.values(mappings).includes(detected)) {
            mappings[header] = detected;
        } else {
            mappings[header] = '';
        }
    }
    return mappings;
}

function buildRows(parsedData, mappings) {
    return parsedData.map(row => {
        const out = { instagram: null, email: null, geo: null, name: null, tiktok: null, phone: null };
        for (const [csvCol, crmField] of Object.entries(mappings)) {
            if (crmField && row[csvCol] != null && String(row[csvCol]).trim()) {
                out[crmField] = String(row[csvCol]).trim();
            }
        }
        return out;
    }).filter(r => r.instagram || r.email);
}

export default function CsvImportModal({ open, onClose, onImported }) {
    const queryClient = useQueryClient();
    const [step, setStep] = useState('choose');
    const [application, setApplication] = useState('AR Drawing');
    const [tagLabel, setTagLabel] = useState('');
    const [inputMode, setInputMode] = useState('file');
    const [pasteText, setPasteText] = useState('');
    const [fileName, setFileName] = useState('');
    const [parsedData, setParsedData] = useState([]);
    const [csvHeaders, setCsvHeaders] = useState([]);
    const [mappings, setMappings] = useState({});
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [claimIds, setClaimIds] = useState(new Set());
    const [claiming, setClaiming] = useState(false);
    const [hasHeaders, setHasHeaders] = useState(true);
    const [previewRows, setPreviewRows] = useState([]);
    const [selectedRows, setSelectedRows] = useState(new Set());
    const fileRef = useRef(null);
    const rawTextRef = useRef('');

    if (!open) return null;

    const parseFile = (text, useHeaders) => {
        const cleaned = text.replace(/^\uFEFF/, '');
        const parsed = Papa.parse(cleaned, {
            header: useHeaders,
            skipEmptyLines: true,
            dynamicTyping: false,
        });
        let headers, data;
        if (useHeaders) {
            headers = parsed.meta.fields || [];
            data = parsed.data;
        } else {
            const maxCols = parsed.data.reduce((m, row) => Math.max(m, row.length), 0);
            headers = Array.from({ length: maxCols }, (_, i) => `Колонка ${i + 1}`);
            data = parsed.data.map(row => {
                const obj = {};
                headers.forEach((h, i) => { obj[h] = row[i] || ''; });
                return obj;
            });
        }
        const detected = autoDetectMappings(headers);
        setCsvHeaders(headers);
        setParsedData(data);
        setMappings(detected);
        setResult(null);
        setClaimIds(new Set());
    };

    const handleFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setFileName(file.name);
        const reader = new FileReader();
        reader.onload = (ev) => {
            rawTextRef.current = ev.target.result;
            parseFile(ev.target.result, hasHeaders);
        };
        reader.readAsText(file);
    };

    const handleContinue = () => {
        if (inputMode === 'text' && pasteText.trim()) {
            rawTextRef.current = pasteText;
            parseFile(pasteText, hasHeaders);
        }
        if (csvHeaders.length > 0 || (inputMode === 'text' && pasteText.trim())) {
            if (csvHeaders.length > 0) {
                setStep('map');
            }
        }
    };

    const handleContinueText = () => {
        rawTextRef.current = pasteText;
        const cleaned = pasteText.replace(/^\uFEFF/, '');
        const parsed = Papa.parse(cleaned, {
            header: hasHeaders,
            skipEmptyLines: true,
            dynamicTyping: false,
        });
        let headers, data;
        if (hasHeaders) {
            headers = parsed.meta.fields || [];
            data = parsed.data;
        } else {
            const maxCols = parsed.data.reduce((m, row) => Math.max(m, row.length), 0);
            headers = Array.from({ length: maxCols }, (_, i) => `Колонка ${i + 1}`);
            data = parsed.data.map(row => {
                const obj = {};
                headers.forEach((h, i) => { obj[h] = row[i] || ''; });
                return obj;
            });
        }
        if (headers.length > 0 && data.length > 0) {
            const detected = autoDetectMappings(headers);
            setCsvHeaders(headers);
            setParsedData(data);
            setMappings(detected);
            setResult(null);
            setClaimIds(new Set());
            setStep('map');
        }
    };

    const handleHeadersToggle = (checked) => {
        setHasHeaders(checked);
        if (rawTextRef.current) {
            parseFile(rawTextRef.current, checked);
        }
    };

    const handleMappingChange = (header, value) => {
        setMappings(prev => ({ ...prev, [header]: value }));
    };

    const usedFields = new Set(Object.values(mappings).filter(Boolean));

    const handleImport = async () => {
        setLoading(true);
        try {
            const transformedRows = buildRows(parsedData, mappings);
            if (transformedRows.length === 0) {
                setResult({ ok: false, error: 'Нет строк с email или instagram' });
                setStep('result');
                return;
            }
            const preview = await previewImport(transformedRows);
            if (preview.ok) {
                setPreviewRows(preview.rows);
                setSelectedRows(new Set(preview.rows.map((_, i) => i).filter((i) => preview.rows[i].action !== 'block')));
                setStep('preview');
            } else {
                setResult({ ok: false, error: preview.error || 'Preview failed' });
                setStep('result');
            }
        } catch (err) {
            setResult({ ok: false, error: err.message });
            setStep('result');
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmImport = async () => {
        setLoading(true);
        try {
            const transformedRows = buildRows(parsedData, mappings);
            const rowsToImport = transformedRows.filter((_, i) => selectedRows.has(i));
            if (rowsToImport.length === 0) {
                setResult({ ok: false, error: 'Не выбрано ни одной строки' });
                setStep('result');
                return;
            }
            const res = await importLeadsCsv({
                rows: rowsToImport,
                application: application || undefined,
                tagLabel: tagLabel || undefined,
            });
            setResult(res);
            setClaimIds(new Set());
            setStep('result');
            queryClient.invalidateQueries({ queryKey: queryKeys.tags.all });
            if (res.created > 0 && onImported) onImported();
        } catch (err) {
            setResult({ ok: false, error: err.message });
            setStep('result');
        } finally {
            setLoading(false);
        }
    };

    const handleClaim = async () => {
        if (claimIds.size === 0) return;
        setClaiming(true);
        try {
            const res = await importLeadsCsv({ claimLeadIds: [...claimIds], application: application || undefined, tagLabel: tagLabel || undefined });
            if (res.claimed > 0) {
                setResult(prev => ({
                    ...prev,
                    claimed: (prev.claimed || 0) + res.claimed,
                    duplicateDetails: prev.duplicateDetails?.map(d =>
                        claimIds.has(d.leadId) ? { ...d, claimed: true } : d
                    )
                }));
                setClaimIds(new Set());
                queryClient.invalidateQueries({ queryKey: queryKeys.tags.all });
                if (onImported) onImported();
            }
        } catch (err) {
            console.error('Claim failed:', err);
        } finally {
            setClaiming(false);
        }
    };

    const toggleClaim = (leadId) => {
        setClaimIds(prev => {
            const next = new Set(prev);
            if (next.has(leadId)) next.delete(leadId);
            else next.add(leadId);
            return next;
        });
    };

    const toggleAllClaims = () => {
        const unclaimed = (result?.duplicateDetails || []).filter(d => !d.claimed);
        if (claimIds.size === unclaimed.length) {
            setClaimIds(new Set());
        } else {
            setClaimIds(new Set(unclaimed.map(d => d.leadId)));
        }
    };

    const resetAll = () => {
        setStep('choose');
        setInputMode('file');
        setPasteText('');
        setFileName('');
        setParsedData([]);
        setCsvHeaders([]);
        setMappings({});
        setHasHeaders(true);
        setResult(null);
        setClaimIds(new Set());
        setPreviewRows([]);
        setSelectedRows(new Set());
        rawTextRef.current = '';
        if (fileRef.current) fileRef.current.value = '';
    };

    const handleClose = () => {
        resetAll();
        onClose();
    };

    const handleBackdrop = () => {};

    const duplicates = result?.duplicateDetails || [];

    const mappingPreview = parsedData.slice(0, 5).map(row => {
        const out = { instagram: null, email: null, geo: null, name: null };
        for (const [csvCol, crmField] of Object.entries(mappings)) {
            if (crmField && ['instagram', 'email', 'geo', 'name'].includes(crmField) && row[csvCol] != null && String(row[csvCol]).trim()) {
                out[crmField] = String(row[csvCol]).trim();
            }
        }
        return out;
    });

    const getSampleValues = (header) => {
        const vals = parsedData
            .map(r => String(r[header] || '').trim())
            .filter(Boolean);
        return vals.slice(0, 3).join(', ') || '—';
    };

    const hasData = inputMode === 'file' ? csvHeaders.length > 0 : pasteText.trim().length > 0;

    const tabStyle = (active) => ({
        padding: '6px 16px',
        fontSize: '13px',
        fontWeight: 500,
        border: 'none',
        borderBottom: active ? '2px solid var(--purple-60)' : '2px solid transparent',
        background: 'none',
        color: active ? 'var(--purple-60)' : 'var(--text-muted)',
        cursor: 'pointer',
        transition: 'all 0.15s',
    });

    const sectionTitle = {
        fontSize: '11px',
        fontWeight: 600,
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    };

    return (
        <div className='manager-modal-backdrop' role='presentation' onClick={handleBackdrop}>
            <div className='manager-modal' style={{ width: step === 'choose' ? '500px' : '780px', transition: 'width 0.2s' }} role='dialog' aria-modal='true'>
                <header className='manager-modal__header'>
                    <h2>Импорт блогеров</h2>
                    <button type='button' className='manager-modal__close' onClick={handleClose} disabled={loading || claiming} aria-label='Закрыть'>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                    </button>
                </header>

                <div className='manager-modal__body' style={{ gap: '12px' }}>

                    {step === 'choose' && (
                        <>
                            <div style={{ display: 'flex', gap: '0', borderBottom: '1px solid var(--neutral-30)', marginBottom: '4px' }}>
                                <button type='button' style={tabStyle(inputMode === 'file')} onClick={() => setInputMode('file')}>Файл</button>
                                <button type='button' style={tabStyle(inputMode === 'text')} onClick={() => setInputMode('text')}>Текст</button>
                            </div>

                            {inputMode === 'file' ? (
                                <>
                                    <p style={{ color: 'var(--text-muted)', fontSize: '12px', margin: 0 }}>
                                        CSV или TXT. Разделители: запятая, точка с запятой.
                                    </p>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <label className='ghost-button' style={{ fontSize: '13px', padding: '7px 16px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                                            Выбрать файл
                                            <input ref={fileRef} type='file' accept='.csv,.txt' onChange={handleFile} style={{ display: 'none' }} />
                                        </label>
                                        {fileName && (
                                            <span style={{ fontSize: '13px', color: 'var(--text-primary)', fontWeight: 500 }}>{fileName}</span>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <p style={{ color: 'var(--text-muted)', fontSize: '12px', margin: 0 }}>
                                        Вставьте данные из таблицы или списка. Каждая строка — отдельная запись.
                                    </p>
                                    <textarea
                                        value={pasteText}
                                        onChange={e => setPasteText(e.target.value)}
                                        placeholder={'instagram,email,geo\n@username,email@mail.com,USA'}
                                        rows={6}
                                        style={{
                                            width: '100%',
                                            padding: '10px 12px',
                                            border: '1px solid var(--neutral-40)',
                                            borderRadius: '6px',
                                            background: 'var(--neutral-0)',
                                            color: 'var(--text-primary)',
                                            fontSize: '13px',
                                            fontFamily: 'monospace',
                                            resize: 'vertical',
                                            outline: 'none',
                                        }}
                                    />
                                </>
                            )}

                            <label style={{ fontSize: '13px', color: 'var(--text-secondary)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                <input
                                    type='checkbox'
                                    checked={hasHeaders}
                                    onChange={e => handleHeadersToggle(e.target.checked)}
                                />
                                Первая строка — заголовки
                            </label>

                            <footer className='manager-modal__footer'>
                                <button type='button' className='ghost-button' onClick={handleClose}>Отмена</button>
                                <button
                                    type='button'
                                    className='primary-button'
                                    disabled={!hasData}
                                    onClick={() => {
                                        if (inputMode === 'text') {
                                            handleContinueText();
                                        } else if (csvHeaders.length > 0) {
                                            setStep('map');
                                        }
                                    }}
                                >
                                    Продолжить
                                </button>
                            </footer>
                        </>
                    )}

                    {step === 'map' && (
                        <>
                            <p style={{ color: 'var(--text-muted)', fontSize: '13px', margin: 0 }}>
                                Найдено <strong style={{ color: 'var(--text-primary)' }}>{parsedData.length}</strong> строк. Сопоставьте колонки с полями CRM:
                            </p>

                            <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--neutral-30)', borderRadius: '8px' }}>
                                <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ background: 'var(--neutral-10)', position: 'sticky', top: 0 }}>
                                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Колонка CSV</th>
                                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Примеры</th>
                                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', width: '160px' }}>Поле CRM</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {csvHeaders.map(header => {
                                            const currentVal = mappings[header] || '';
                                            return (
                                                <tr key={header} style={{ borderTop: '1px solid var(--neutral-20)' }}>
                                                    <td style={{ padding: '6px 12px', fontWeight: 500, color: 'var(--text-primary)' }}>{header}</td>
                                                    <td style={{ padding: '6px 12px', color: 'var(--text-muted)', maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '12px' }}>
                                                        {getSampleValues(header)}
                                                    </td>
                                                    <td style={{ padding: '6px 12px' }}>
                                                        <select
                                                            value={currentVal}
                                                            onChange={e => handleMappingChange(header, e.target.value)}
                                                            style={{
                                                                width: '100%',
                                                                padding: '5px 8px',
                                                                border: '1px solid var(--neutral-40)',
                                                                borderRadius: '6px',
                                                                background: 'var(--neutral-0)',
                                                                color: 'var(--text-primary)',
                                                                fontSize: '13px',
                                                                outline: 'none',
                                                            }}
                                                        >
                                                            <option value=''>— (пропустить)</option>
                                                            {CRM_FIELDS.map(f => (
                                                                <option
                                                                    key={f.value}
                                                                    value={f.value}
                                                                    disabled={usedFields.has(f.value) && currentVal !== f.value}
                                                                >
                                                                    {f.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {mappingPreview.length > 0 && (
                                <>
                                    <div style={sectionTitle}>Предпросмотр (первые {mappingPreview.length} строк)</div>
                                    <div style={{ border: '1px solid var(--neutral-30)', borderRadius: '8px', overflow: 'hidden' }}>
                                        <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr style={{ background: 'var(--neutral-10)' }}>
                                                    <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Instagram</th>
                                                    <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Email</th>
                                                    <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Гео</th>
                                                    <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Имя</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {mappingPreview.map((r, i) => (
                                                    <tr key={i} style={{ borderTop: '1px solid var(--neutral-20)' }}>
                                                        <td style={{ padding: '5px 10px', color: 'var(--text-primary)' }}>{r.instagram || '—'}</td>
                                                        <td style={{ padding: '5px 10px', color: 'var(--text-primary)' }}>{r.email || '—'}</td>
                                                        <td style={{ padding: '5px 10px', color: 'var(--text-primary)' }}>{r.geo || '—'}</td>
                                                        <td style={{ padding: '5px 10px', color: 'var(--text-primary)' }}>{r.name || '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}

                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 500 }}>Приложение:</span>
                                <select
                                    value={application}
                                    onChange={e => setApplication(e.target.value)}
                                    style={{
                                        padding: '5px 10px',
                                        border: '1px solid var(--neutral-40)',
                                        borderRadius: '6px',
                                        background: 'var(--neutral-0)',
                                        color: 'var(--text-primary)',
                                        fontSize: '13px',
                                        minWidth: '200px',
                                    }}
                                >
                                    <option value=''>— не указано —</option>
                                    {APPS.map(a => (
                                        <option key={a.value} value={a.value}>{a.value}</option>
                                    ))}
                                </select>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 500 }}>Тег:</span>
                                <TagPicker value={tagLabel} onChange={setTagLabel} disabled={loading} />
                            </div>

                            <footer className='manager-modal__footer'>
                                <button type='button' className='ghost-button' onClick={() => setStep('choose')} disabled={loading}>Назад</button>
                                <button type='button' className='primary-button' onClick={handleImport} disabled={loading}>
                                    {loading ? 'Проверяем...' : `Загрузить ${parsedData.length} блогеров`}
                                </button>
                            </footer>
                        </>
                    )}

                    {step === 'preview' && (
                        <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                                <span style={{ color: 'var(--text-muted)' }}>Всего: <strong style={{ color: 'var(--text-primary)' }}>{previewRows.length}</strong></span>
                                <span style={{ color: 'var(--neutral-40)' }}>·</span>
                                <span style={{ color: '#22c55e', fontWeight: 600 }}>Выбрано: {selectedRows.size}</span>
                                {(() => {
                                    const dupCount = previewRows.filter(r => r.hasDuplicate).length;
                                    return dupCount > 0 ? (
                                        <>
                                            <span style={{ color: 'var(--neutral-40)' }}>·</span>
                                            <span style={{ color: '#eab308', fontWeight: 600 }}>Дубликатов: {dupCount}</span>
                                        </>
                                    ) : null;
                                })()}
                            </div>

                            <div style={{ maxHeight: '420px', overflowY: 'auto', border: '1px solid var(--neutral-30)', borderRadius: '8px' }}>
                                <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ background: 'var(--neutral-10)', position: 'sticky', top: 0 }}>
                                            <th style={{ padding: '8px 10px', width: '30px' }}>
                                                <input
                                                    type='checkbox'
                                                    checked={(() => { const sel = previewRows.map((_, i) => i).filter((i) => previewRows[i].action !== 'block'); return sel.length > 0 && sel.every((i) => selectedRows.has(i)); })()}
                                                    onChange={() => {
                                                        const sel = previewRows.map((_, i) => i).filter((i) => previewRows[i].action !== 'block');
                                                        if (sel.every((i) => selectedRows.has(i))) {
                                                            setSelectedRows(new Set());
                                                        } else {
                                                            setSelectedRows(new Set(sel));
                                                        }
                                                    }}
                                                />
                                            </th>
                                            <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Email / Instagram</th>
                                            <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Имя</th>
                                            <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Дубли</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {previewRows.map((row, i) => (
                                            <tr key={i} style={{ borderTop: '1px solid var(--neutral-20)', opacity: row.action === 'block' ? 0.4 : (selectedRows.has(i) ? 1 : 0.6) }}>
                                                <td style={{ padding: '6px 10px' }}>
                                                    <input
                                                        type='checkbox'
                                                        disabled={row.action === 'block'}
                                                        checked={selectedRows.has(i)}
                                                        onChange={() => {
                                                            setSelectedRows(prev => {
                                                                const next = new Set(prev);
                                                                if (next.has(i)) next.delete(i);
                                                                else next.add(i);
                                                                return next;
                                                            });
                                                        }}
                                                    />
                                                </td>
                                                <td style={{ padding: '6px 10px', color: 'var(--text-primary)' }}>{row.email || row.instagram || '—'}</td>
                                                <td style={{ padding: '6px 10px', color: 'var(--text-primary)' }}>{row.realName || '—'}</td>
                                                <td style={{ padding: '6px 10px' }}>
                                                    {row.error && <span style={{ color: '#ef4444', fontSize: '11px' }}>{row.error}</span>}
                                                    {!row.hasDuplicate && !row.error && row.inFileDuplicateOf == null && (
                                                        <span style={{ color: '#22c55e', fontSize: '11px' }}>✓ Уникальный</span>
                                                    )}
                                                    {row.inFileDuplicateOf != null && (
                                                        <span style={{ padding: '1px 6px', borderRadius: 4, background: 'rgba(234,179,8,0.12)', color: '#a16207', fontSize: '10px', fontWeight: 600, marginRight: 4 }}>Дубль строки {row.inFileDuplicateOf + 1}</span>
                                                    )}
                                                    {row.action === 'take' && (
                                                        <span style={{ padding: '1px 6px', borderRadius: 4, background: 'rgba(59,130,246,0.12)', color: '#2563eb', fontSize: '10px', fontWeight: 600, marginRight: 4 }}>Перенесём</span>
                                                    )}
                                                    {row.action === 'attach' && (
                                                        <span style={{ padding: '1px 6px', borderRadius: 4, background: 'rgba(168,85,247,0.12)', color: '#9333ea', fontSize: '10px', fontWeight: 600, marginRight: 4 }}>Привяжем к блогеру</span>
                                                    )}
                                                    {row.action === 'block' && (
                                                        <span style={{ padding: '1px 6px', borderRadius: 4, background: 'rgba(239,68,68,0.12)', color: '#ef4444', fontSize: '10px', fontWeight: 600, marginRight: 4 }}>Нельзя</span>
                                                    )}
                                                    {row.hasDuplicate && row.duplicates.map((d, j) => {
                                                        const isOrig = !d.isDuplicate;
                                                        const field = d.field === 'instagram' ? 'IG' : d.field === 'email' ? 'EM' : 'TT';
                                                        let badge = null;
                                                        if (d.isAmbassador) {
                                                            badge = <span style={{ padding: '1px 6px', borderRadius: 4, background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: '10px', fontWeight: 500 }}>Амбассадор</span>;
                                                        } else if (d.isFree) {
                                                            badge = <span style={{ padding: '1px 6px', borderRadius: 4, background: 'rgba(34,197,94,0.1)', color: '#22c55e', fontSize: '10px', fontWeight: 500 }}>Свободен · {d.daysInStatus} дн.</span>;
                                                        } else {
                                                            badge = <span style={{ padding: '1px 6px', borderRadius: 4, background: 'rgba(245,158,11,0.1)', color: '#d97706', fontSize: '10px', fontWeight: 500 }}>Активен · {d.daysInStatus} дн.</span>;
                                                        }
                                                        return (
                                                            <div key={j} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', marginBottom: '2px' }}>
                                                                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: isOrig ? '#22c55e' : '#eab308', display: 'inline-block', flexShrink: 0 }} />
                                                                <span style={{ color: 'var(--text-muted)' }}>{field} · {d.ownerName || d.scout || '—'} · {d.statusLabel}</span>
                                                                {badge}
                                                            </div>
                                                        );
                                                    })}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <footer className='manager-modal__footer'>
                                <span style={{ marginRight: 'auto', fontSize: '12px', color: 'var(--text-muted)' }}>
                                    Создать {previewRows.filter(r => (r.action === 'create' || r.action === 'attach') && r.inFileDuplicateOf == null).length}
                                    {' · '}Дубли в файле {previewRows.filter(r => r.inFileDuplicateOf != null).length}
                                    {' · '}Перенести {previewRows.filter(r => r.action === 'take').length}
                                    {' · '}Нельзя {previewRows.filter(r => r.action === 'block').length}
                                </span>
                                <button type='button' className='ghost-button' onClick={() => setStep('map')}>Назад</button>
                                <button
                                    type='button'
                                    className='primary-button'
                                    onClick={handleConfirmImport}
                                    disabled={loading || selectedRows.size === 0}
                                >
                                    {loading ? 'Создаём...' : `Импортировать (${selectedRows.size})`}
                                </button>
                            </footer>
                        </>
                    )}

                    {step === 'result' && result && (
                        <>
                            {result.ok !== false ? (
                                <>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        <p style={{ color: '#22c55e', fontWeight: 600, fontSize: '14px', margin: 0 }}>
                                            Создано новых: {result.created}
                                        </p>
                                        {result.duplicates > 0 && (
                                            <p style={{ color: '#eab308', fontSize: '13px', margin: 0 }}>Дубли внутри файла: {result.duplicates}</p>
                                        )}
                                        {result.copied > 0 && (
                                            <p style={{ color: '#2563eb', fontSize: '13px', margin: 0 }}>Перенесено: {result.copied}</p>
                                        )}
                                        {result.blocked > 0 && (
                                            <p style={{ color: '#ef4444', fontSize: '13px', margin: 0 }}>Заблокировано: {result.blocked}</p>
                                        )}
                                        {result.claimed > 0 && (
                                            <p style={{ color: '#22c55e', fontSize: '13px', margin: 0 }}>Забрано себе: {result.claimed}</p>
                                        )}
                                        {result.errors?.length > 0 && (
                                            <div style={{ color: '#ef4444', marginTop: '4px' }}>
                                                <p style={{ margin: '0 0 2px', fontWeight: 500 }}>Ошибки:</p>
                                                {result.errors.map((e, i) => (
                                                    <p key={i} style={{ fontSize: '12px', margin: 0 }}>Строка {e.row}: {e.error}</p>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <footer className='manager-modal__footer'>
                                        <button type='button' className='ghost-button' onClick={resetAll}>Импортировать ещё</button>
                                        <button type='button' className='primary-button' onClick={handleClose}>Готово</button>
                                    </footer>
                                </>
                            ) : (
                                <>
                                    <p style={{ color: '#ef4444', fontWeight: 500 }}>Ошибка: {result.error}</p>
                                    <footer className='manager-modal__footer'>
                                        <button type='button' className='ghost-button' onClick={resetAll}>Попробовать снова</button>
                                    </footer>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
